/* global axios */
import CondoAccessAPI from '../../api/condoAccess';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

export const state = {
  condoList: [],
  mainList: [],
  condoDetails: {},
  documentDetails: {},
  noticeList: [],
  momList: [],
  houseRuleList: [],
  accessibleCondoIds: [],
  teamCondoAccesses: {},
  residentList: [],
};

export const mutations = {
  setCondoList(state, newList) { state.condoList = newList; },
  setCondoDetails(state, details) { state.condoDetails = details; },
  setNoticeList(state, list) { state.noticeList = list; },
  setMOMList(state, list) { state.momList = list; },
  setHouseRuleList(state, list) { state.houseRuleList = list; },
  setDocumentDetails(state, details) { state.documentDetails = details; },
  setMainList(state, list) { state.mainList = list; },
  setAccessibleCondoIds(state, condoIds) { state.accessibleCondoIds = condoIds; },
  setTeamCondoAccesses(state, { teamId, condos }) {
    state.teamCondoAccesses = { ...state.teamCondoAccesses, [teamId]: condos };
  },
  setResidentList(state, list) { state.residentList = list; }
};

export const getters = {
  getAccessibleCondoIds: state => state.accessibleCondoIds,
  getFilteredCondoList: (state, getters, rootState, rootGetters) => {
    if (rootGetters['getCurrentAccountId']) {
      const currentUser = rootGetters['getCurrentUser'];
      if (currentUser && currentUser.role === 'administrator') {
        return state.condoList;
      }
    }

    if (state.accessibleCondoIds.length === 0) {
      return [];
    }

    return state.condoList.filter(condo =>
      state.accessibleCondoIds.includes(condo.condo_id)
    );
  },
  getTeamCondos: state => teamId => {
    return state.teamCondoAccesses[teamId] || [];
  },
};

export const actions = {
  async fetchAccessibleCondos({ commit }) {
    try {
      const response = await CondoAccessAPI.getAccessibleCondos();
      commit('setAccessibleCondoIds', response.data.condo_ids);
      return response.data;
    } catch (err) {
      throw err;
    }
  },

  async fetchTeamCondos({ commit }, teamId) {
    try {
      const response = await CondoAccessAPI.getTeamCondos(teamId);
      commit('setTeamCondoAccesses', { teamId: teamId, condos: response.data.condos });
      return response.data;
    } catch (err) {
      throw err;
    }
  },

  async assignCondoToTeam({ dispatch }, { teamId, condoId, condoName, datasetId }) {
    try {
      await CondoAccessAPI.assignCondoToTeam({ teamId, condoId, condoName, datasetId });
      await dispatch('fetchTeamCondos', teamId);
      await dispatch('fetchAccessibleCondos');
    } catch (err) {
      throw err;
    }
  },

  async batchAssignCondosToTeam({ dispatch }, { teamId, condos }) {
    try {
      await CondoAccessAPI.batchAssignCondosToTeam({ teamId, condos });
      await dispatch('fetchTeamCondos', teamId);
      await dispatch('fetchAccessibleCondos');
    } catch (err) {
      throw err;
    }
  },

  async removeCondoFromTeam({ dispatch }, { teamId, condoId }) {
    try {
      await CondoAccessAPI.removeCondoFromTeam({ teamId, condoId });
      await dispatch('fetchTeamCondos', teamId);
      await dispatch('fetchAccessibleCondos');
    } catch (err) {
      throw err;
    }
  },

  async createCondo({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const formData = new FormData();

      for (const key in payload) {
        const value = payload[key];
        if (value instanceof File) {
          formData.append(key, value);
        } else if (Array.isArray(value)) {
          value.forEach((v, i) => { formData.append(`${key}[${i}]`, v); });
        } else {
          formData.append(key, value);
        }
      }

      const res = await axios.post(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchCondos({ rootGetters, commit }, { page = 1, pageSize = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos?page=${page}&page_size=${pageSize}`)
      );
      commit('setCondoList', res.data.results);
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async editCondo({ rootGetters }, { condoId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const formData = new FormData();
      for (const key in payload) {
        if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      }

      const res = await axios.put(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async deleteCondo({ rootGetters }, condoId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}`)
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchCondo({ rootGetters, commit }, condoId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}`)
      );
      commit('setCondoDetails', res.data);
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async uploadDoc({ rootGetters }, { condoId, formData }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/documents/file`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchDocuments({ rootGetters, commit }, { condoId, documentType = 'notice', page = 1, pageSize = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const cancelToken = axios.CancelToken.source();

      const res = await axios.get(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/documents?document_type=${documentType}&page=${page}&page_size=${pageSize}`),
        { cancelToken: cancelToken.token }
      );

      commit('setMainList', res.data.results);
      return res.data.results;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request cancelled');
        return [];
      }
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchDocument({ rootGetters, commit }, { condoId, documentId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/documents/${documentId}`)
      );
      commit('setDocumentDetails', res.data);
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async updateDocument({ rootGetters }, { condoId, documentId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/documents/${documentId}`),
        payload
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async deleteDocument({ rootGetters }, { condoId, documentId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/documents/${documentId}`)
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async uploadResident({ rootGetters }, { condoId, formData }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.post(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/residents/import`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async fetchResidentList({ rootGetters, commit }, { condoId, query = '', page = 1, pageSize = 10 }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const cancelToken = axios.CancelToken.source();

      const res = await axios.get(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/residents?search=${query}&page=${page}&page_size=${pageSize}`),
        { cancelToken: cancelToken.token }
      );

      commit('setResidentList', res.data.results);
      return res.data;
    } catch (err) {
      if (axios.isCancel(err)) {
        console.log('Request cancelled');
        return [];
      }
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async exportResidents({ rootGetters }, { condoId, format }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.post(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/residents/export?format=${format}`),
        {},
        { responseType: 'blob' }
      );
      return res;
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async deleteResident({ rootGetters }, { condoId, residentId }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.delete(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/residents/${residentId}`)
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },

  async editResident({ rootGetters }, { condoId, residentId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      await axios.put(
        knowledgeProxy(accountId, `/condo/accounts/${accountId}/condos/${condoId}/residents/${residentId}`),
        payload
      );
    } catch (err) {
      if (err.response?.data?.detail) {
        throw err.response.data.detail.map(d => d.msg);
      } else {
        throw err;
      }
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
